import CIcon from '@coreui/icons-react';
import React, { useMemo, useRef, useState } from 'react';
import { components, ValueContainerProps } from 'react-select';
import _AsyncSelect from 'react-select/async';

import { ReactComponent as SearchIcon } from '../../../../assets/svgs/search.svg';
import { useLocalize } from '../../../../hooks/useLocalize';
import { useSearchLocation } from '../../../../hooks/useSearchLocation';
import { useQueryLocationByGooglePlaceById } from '../../../../queries/locationByGooglePlaceId/hooks/useQueryLocationByGooglePlaceId';
import { ReactComponent as DangerIcon } from '../danger.svg';
import { SearchResultItem } from '../searchResultItem/SearchResultItem';
import { SearchLocation } from '../utils';
import { SearchResultOption } from '../utils';
import { iconMap } from '../utils';

class AsyncSelect extends _AsyncSelect<SearchResultOption, false> { }

export interface LocationSearchInputProps {
  placeholder?: string;
  allowAddressSearch?: boolean;
  location?: SearchLocation;
  setLocation: (newLocation: SearchLocation | undefined) => void;
  error?: string;
  bounds?: google.maps.LatLngBoundsLiteral;
  dataTestId?: string;
  locale: string;
  styles: any;
  asyncSelectStyles: any;
  extraFilter?: (location?: AutoCompleteAddressItem) => boolean;
  editablePlaceholder?: string;
  customIconType?: string;
  valueLabel?: keyof AutoCompleteAddressItem;
  selectedHub?: SearchLocation | undefined;
}

export const LocationSearchInputDesktop: React.FC<LocationSearchInputProps> = ({
  placeholder, allowAddressSearch = true,
  location,
  setLocation,
  error,
  bounds,
  dataTestId,
  locale,
  styles,
  asyncSelectStyles,
  extraFilter,
  editablePlaceholder,
  customIconType,
  valueLabel,
  selectedHub = undefined,
}) => {
  const { refetch: refetchLocationData } = useQueryLocationByGooglePlaceById({
    variables: {
      id: '',
    },
    skip: true,
  });
  const nativeRef = useRef<HTMLDivElement | null>(null);
  const { translate: t } = useLocalize();
  const { handleSearch } = useSearchLocation(allowAddressSearch, selectedHub, bounds, extraFilter);
  const [inputValue, setInputValue] = useState<string>();
  const value = useMemo(() => location ? {
    title: location.title,
    subtitle: location.subtitle,
    type: location.type,
    label: valueLabel ? String(location?.[valueLabel]) : location.formattedAddress,
    innerresult: location,
  } : undefined, [location]);

  const Icon = !location ? SearchIcon : customIconType ? iconMap[customIconType as keyof typeof iconMap] : iconMap[location.type as keyof typeof iconMap];
  const defaultEditablePlaceholder = t(allowAddressSearch ? 'search_placeholder_hub_or_address' : 'search_placeholder_hub');

  const ValueContainer = useMemo(() => (props: React.PropsWithChildren<ValueContainerProps<SearchResultOption, false>>) => (
    <>
      {Icon && <Icon className={styles.icon} />}
      {!value && <span className="direction-placeholder"><b>{placeholder}</b></span>}
      <components.ValueContainer {...props} className={styles.valueContainer} />
      {value && <span className="clear-icon" onClick={() => {
        props.clearValue();
        setLocation(undefined);
        setTimeout(() => nativeRef.current?.querySelector('input')?.focus(), 1);
      }}>
        <CIcon size="lg" name="cil-x-circle" />
      </span>}
    </>
  ), [Icon, value, placeholder, setLocation]);

  const loadOptions = (inputValue: string, callback: any) => {
    setInputValue(inputValue);
    handleSearch(inputValue, (results) =>  {
      if (results !== 'NOT_RESULTS_FOUND') {
        callback(results);
      } else {
        callback([]);
      }
    });
  };

  const fetchLocation = async (innerResult: AutoCompleteAddressItem | undefined) => {
    try {
      if (innerResult?.hubId !== null && innerResult?.googlePlaceId)  {
        const resp = await refetchLocationData({
          id: innerResult?.googlePlaceId,
        });
        const locationData = resp?.data?.locationByGooglePlaceId;
        if (locationData) {
          setLocation({
            ...innerResult,
            lat: locationData?.position?.lat,
            lng: locationData?.position?.lng,
            isHub: !!innerResult?.hubId,
          });
        }
        return;
      }
      setLocation(innerResult);

    } catch (err) {
      console.error('Error fetching location:', err);
    }
  };

  const minSymbolsThreshold: number = inputValue?.match(/[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf]/) ? 2 : 3;

  return (
    <div className={styles.componentWrapper} ref={nativeRef} data-testid={dataTestId}>
      <AsyncSelect
        className={`${styles.locationSearchInputDesktop} destination-select`}
        components={{
          ValueContainer: ValueContainer,
          Menu: (props) => (
            (inputValue?.length ?? 0) >= minSymbolsThreshold ? <components.Menu {...props} className={styles.suggestionList} /> : null
          ),
          Option: (props) => (
            <components.Option {...props} className="suggestion-item">
              <SearchResultItem
                {...props.data}
                className="suggestion-item__content"
                style={{ padding: '0.5rem 1rem' }}
              />
            </components.Option>
          ),
        }}
        styles={asyncSelectStyles}
        key="destination"
        loadOptions={loadOptions}
        placeholder={editablePlaceholder || defaultEditablePlaceholder}
        noOptionsMessage={({ inputValue }) => inputValue.length >= minSymbolsThreshold
          ? t(`search_no_results_found${allowAddressSearch ? '_hub_or_address' : '_hub'}`)
          : null
        }
        value={value}
        onChange={(option) => {
          const innerresult = option?.innerresult;
          fetchLocation(innerresult);
        }}
        isClearable={!!value}
        backspaceRemovesValue={false}
        escapeClearsValue={false}
      />
      {!!error && <div className="error-message"><DangerIcon /><span>{error}</span></div>}
    </div>
  );
};
